import LicensePrincingCard, {
    LicensePrincingCardVariants,
} from "@/components/cards/LicensePrincingCard";
import CenteredSpinner from "@/components/CenteredSpinner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useFullLicensesDetails from "@/hooks/useFullLicensesDetails";
import { cn } from "@/lib/utils";
import routes from "@/router/routes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LicenseTabs from "./licenseTabs";

type LicenseSelectorProps = {
    className?: string;
    variant?: LicensePrincingCardVariants;
    defaultTab: LicenseTabs;
};

const LicenseSelector: React.FC<LicenseSelectorProps> = ({
    className,
    variant = LicensePrincingCardVariants.Default,
    defaultTab,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        fullLicensesDetails,
        isLoading: isLicenseLoading,
        error,
    } = useFullLicensesDetails();
    const [isMonthly, setIsMonthly] = useState(true);

    const paidLicenses = useMemo(
        () =>
            fullLicensesDetails?.active.filter(
                (license) => !license.name.includes("Trial"),
            ),
        [fullLicensesDetails],
    );

    const enterpriseLicenses = useMemo(
        () => paidLicenses?.filter((license) => license.isEnterprise),
        [paidLicenses],
    );

    const publicLicenses = useMemo(
        () => paidLicenses?.filter((license) => !license.isEnterprise),
        [paidLicenses],
    );

    if (isLicenseLoading) {
        return (
            <div className="h-full w-full">
                <CenteredSpinner />
            </div>
        );
    }

    if (error) {
        console.error(error);

        return null;
    }

    // B2C offer unplugged: price, description, features, checkout and billing portal all came
    // from Stripe, which is removed from the SDK (B2B-only product for now). Cards still render
    // from the backend licenses with a disabled button, until the Stripe calls are wired back
    // for B2C.
    const renderPublicLicenseCards = (licenses: typeof paidLicenses) => {
        if (!licenses?.length) return null;

        return (
            <div className="flex w-full flex-wrap justify-center gap-4 p-1">
                {licenses.map((license) => {
                    const isCurrent = fullLicensesDetails?.active?.some(
                        (userLicense) => userLicense.id === license.id,
                    );

                    return (
                        <LicensePrincingCard
                            key={license.id}
                            buttonText={t("license.iSubscribe")}
                            description=""
                            isCurrent={isCurrent}
                            licenseTab={LicenseTabs.PUBLIC}
                            title={license.name}
                            variant={variant}
                            disabled
                        />
                    );
                })}
            </div>
        );
    };

    const renderEnterpriseLicenseCards = (licenses: typeof paidLicenses) => {
        if (!licenses?.length) return null;

        const isCurrent = fullLicensesDetails?.active?.some((userLicense) =>
            licenses.some((license) => license.id === userLicense.id),
        );

        return (
            <div className="flex w-full flex-wrap justify-center gap-4 p-1">
                <LicensePrincingCard
                    key="enterprise"
                    buttonText={t("license.enterprise.contactUs")}
                    description={t("license.enterprise.description")}
                    features={[
                        t("license.enterprise.features.adminPortal"),
                        t("license.enterprise.features.simplifiedDeployment"),
                        t("license.enterprise.features.exchangeCompatibility"),
                        t("license.enterprise.features.multiPlatform"),
                        t("license.enterprise.features.dataSecurity"),
                        t("license.enterprise.features.signatureManagement"),
                    ]}
                    isCurrent={isCurrent}
                    licenseTab={LicenseTabs.ENTERPRISE}
                    onClick={async () => {
                        navigate(routes.enterpriseForm);
                    }}
                    price={450}
                    title={t("license.enterprise.b2b")}
                    variant={variant}
                />
            </div>
        );
    };

    return (
        <div
            className={cn(
                "text-text-main-title relative flex w-full flex-col overflow-x-hidden",
                className,
            )}
        >
            <main className="relative flex flex-col items-center pb-6">
                <div className="mt-2 flex items-center gap-2 text-sm text-text-body">
                    <span
                        className={`w-15 text-right ${isMonthly ? "font-semibold" : ""}`}
                    >
                        {t("license.monthly")}
                    </span>
                    <Switch
                        checked={!isMonthly}
                        onCheckedChange={(checked) => setIsMonthly(!checked)}
                    />
                    <span
                        className={`w-15 ${!isMonthly ? "font-semibold" : ""}`}
                    >
                        {t("license.yearly")}
                    </span>
                </div>

                <Tabs
                    className="mt-3 w-full max-w-[1280px]"
                    defaultValue={defaultTab}
                >
                    <TabsList
                        className="grid w-full grid-cols-2"
                        variant="modern"
                    >
                        <TabsTrigger value="public" variant="modern">
                            {t("license.tabs.public")}
                        </TabsTrigger>
                        <TabsTrigger value="enterprise" variant="modern">
                            {t("license.tabs.enterprise")}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="public" variant="modern">
                        {renderPublicLicenseCards(publicLicenses)}
                    </TabsContent>

                    <TabsContent value="enterprise" variant="modern">
                        {renderEnterpriseLicenseCards(enterpriseLicenses)}
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default LicenseSelector;
